import { useCallback, useEffect, useState } from "react";
import { Lock, ScrollText, RefreshCw, Monitor, Smartphone, MapPin, Map } from "lucide-react";
import { LoginMap } from "@/components/LoginMap";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TONE = (a, s) => {
  if (s >= 400 || /failed/i.test(a)) return "bg-rose-50 text-rose-700 border-rose-200";
  if (/login|logout/i.test(a)) return "bg-blue-50 text-blue-700 border-blue-200";
  if (/approve|post|award/i.test(a)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const Unlock = ({ onUnlocked }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/activity-logs/unlock", { password: pw });
      onUnlocked(r.data.logs_token);
    } catch (ex) {
      setErr(errMsg(ex));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mx-auto max-w-sm py-4 text-center" data-testid="logs-unlock-form">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><Lock className="h-5 w-5" /></span>
      <p className="mt-3 text-sm font-semibold text-slate-900">Activity logs are password protected</p>
      <p className="text-xs text-slate-500">Enter the activity logs password. Access stays open for 15 minutes.</p>
      <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Logs password" className="mt-4" testId="logs-password-input" autoFocus />
      {err && <p className="mt-2 text-xs text-rose-600" data-testid="logs-unlock-error">{err}</p>}
      <Button type="submit" disabled={!pw || busy} className="mt-3 w-full" data-testid="logs-unlock-btn">{busy ? "Checking..." : "Unlock logs"}</Button>
    </form>
  );
};

const LogTable = ({ items }) => (
  <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200">
    <table className="w-full text-xs">
      <thead className="sticky top-0 z-10 text-left">
        <tr>{["Time", "User", "Action", "Detail", "IP address", "Location", "OS", "Browser", "Device", "Status"].map((h) => <th key={h} className="whitespace-nowrap px-2.5 py-2">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.map((l) => (
          <tr key={l.id} data-testid={`log-row-${l.id}`}>
            <td className="whitespace-nowrap px-2.5 py-1.5 text-slate-500">{fmtDateTime(l.at)}</td>
            <td className="whitespace-nowrap px-2.5 py-1.5"><p className="font-semibold text-slate-800">{l.user_name}</p><p className="text-[10px] text-slate-500">{l.user_email}</p></td>
            <td className="whitespace-nowrap px-2.5 py-1.5"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TONE(l.action, l.status)}`}>{l.action}</span></td>
            <td className="max-w-[220px] truncate px-2.5 py-1.5 text-slate-600" title={l.detail}>{l.detail}</td>
            <td className="whitespace-nowrap px-2.5 py-1.5 font-mono text-[11px]">{l.ip}</td>
            <td className="whitespace-nowrap px-2.5 py-1.5" data-testid={`log-location-${l.id}`}>
              <p className="flex items-center gap-1"><MapPin className="h-3 w-3 text-blue-600" />{l.location || "Unknown"}</p>
              {l.gps && <a href={`https://www.google.com/maps?q=${l.gps.lat},${l.gps.lon}`} target="_blank" rel="noreferrer" className="text-[10px] font-medium text-emerald-600 underline" data-testid={`log-gps-${l.id}`}>GPS {l.gps.lat}, {l.gps.lon} (±{l.gps.accuracy_m} m)</a>}
            </td>
            <td className="whitespace-nowrap px-2.5 py-1.5">{l.os}</td>
            <td className="whitespace-nowrap px-2.5 py-1.5">{l.browser}</td>
            <td className="whitespace-nowrap px-2.5 py-1.5">{l.device === "Desktop" ? <Monitor className="inline h-3.5 w-3.5 text-slate-500" /> : <Smartphone className="inline h-3.5 w-3.5 text-slate-500" />} {l.device}</td>
            <td className={`px-2.5 py-1.5 font-semibold ${l.status >= 400 ? "text-rose-600" : "text-emerald-600"}`}>{l.status}</td>
          </tr>
        ))}
        {!items.length && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">No log entries</td></tr>}
      </tbody>
    </table>
  </div>
);

const SEL = "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs";

const LogViewer = ({ token }) => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", user: "", action: "" });
  const load = useCallback(() => {
    api.get("/activity-logs", { params: f, headers: { "X-Logs-Token": token } }).then((r) => setData(r.data)).catch(() => setData({ items: [], users: [], actions: [], total: 0 }));
  }, [f, token]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  if (!data) return <p className="py-6 text-center text-sm text-slate-500">Loading...</p>;
  return (
    <div className="min-w-0 space-y-3" data-testid="logs-viewer">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={f.q} onChange={set("q")} placeholder="Search user, IP, location, OS, action..." className="h-8 w-full text-xs sm:w-56" data-testid="logs-search" />
        <select value={f.user} onChange={set("user")} className={`${SEL} min-w-0 max-w-full`} data-testid="logs-filter-user">
          <option value="">All users</option>{data.users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={f.action} onChange={set("action")} className={SEL} data-testid="logs-filter-action">
          <option value="">All actions</option>{data.actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Button size="sm" variant="outline" className="h-8" onClick={load} data-testid="logs-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
        <span className="ml-auto text-[11px] text-slate-500" data-testid="logs-count">Showing {data.items.length} of {data.total} entries</span>
      </div>
      <LogTable items={data.items} />
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
    <Icon className="h-3.5 w-3.5" />{children}
  </button>
);

const Unlocked = ({ token }) => {
  const [tab, setTab] = useState("logs");
  return (
    <div className="min-w-0 space-y-3">
      <div className="inline-flex rounded-lg bg-slate-100 p-1">
        <TabBtn active={tab === "logs"} onClick={() => setTab("logs")} icon={ScrollText} testId="tab-logs">Logs</TabBtn>
        <TabBtn active={tab === "map"} onClick={() => setTab("map")} icon={Map} testId="tab-map">Sign-in map</TabBtn>
      </div>
      {tab === "logs" ? <LogViewer token={token} /> : <LoginMap token={token} />}
    </div>
  );
};

export const ActivityLogsButton = () => {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const change = (o) => {
    setOpen(o);
    if (!o) setToken(null);
  };
  return (
    <>
      <Button variant="outline" size="icon" onClick={() => setOpen(true)} title="Activity logs" aria-label="Activity logs" data-testid="btn-activity-logs"><ScrollText className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={change}>
        <DialogContent className="max-h-[92vh] w-[96vw] max-w-6xl overflow-y-auto overflow-x-hidden sm:max-w-6xl [&>*]:min-w-0" data-testid="dialog-activity-logs">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><ScrollText className="h-4 w-4 text-blue-600" />User activity logs</DialogTitle></DialogHeader>
          {token ? <Unlocked token={token} /> : <Unlock onUnlocked={setToken} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
